import { Box, Chip, Divider, Stack, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { CarrierType } from '@yard/app/api'
import { useYardStores } from '@yard/AppProvider'
import { OcuppancyWithFilterData } from '@yard/stores/OccupancyStore'
import {
  ContainerCargoFlags,
  ContainerDirection,
  collectCargoFlags,
  hasAnyCargoFlag,
  inferDirection,
} from '@yard/utils/slot-tooltip.utils'
import { observer } from 'mobx-react-lite'
import { FC, ReactNode } from 'react'

// L2.4 — Slot hover tooltip.
//
// Rendered as the `title` payload of an MUI Tooltip wrapped around an
// occupied slot cell. Lists every occupant in the slot (multi-stack slots
// can have up to maxTiers containers, typically 5) so the planner can spot
// what's in the stack without opening the detail drawer.
//
// Empty slots never render this component — the parent skips the Tooltip
// wrapper entirely for `occupancies.length === 0`.

interface Props {
  occupancies: OcuppancyWithFilterData[]
}

export const YardSlotTooltipContent: FC<Props> = observer(({ occupancies }) => {
  const { t } = useTranslate()
  const { companyStore, carrierVisitStore } = useYardStores()

  if (occupancies.length === 0) return null

  return (
    <Stack gap={1} sx={{ minWidth: 240, py: 0.5 }}>
      {occupancies.map((occ, idx) => (
        <Box key={`${occ.journeyId}-${idx}`}>
          {idx > 0 && <Divider sx={{ mb: 1, borderColor: 'rgba(255,255,255,0.18)' }} />}
          <OccupancyTooltipBlock
            occ={occ}
            operatorName={
              occ.operatorId != null ? companyStore.displayNameFor(occ.operatorId) : undefined
            }
            inboundCarrierName={resolveCarrierVisitName(
              carrierVisitStore.items,
              occ.inboundCarrierVisitId,
            )}
            outboundCarrierName={resolveCarrierVisitName(
              carrierVisitStore.items,
              occ.outboundCarrierVisitId,
            )}
            t={t}
          />
        </Box>
      ))}
    </Stack>
  )
})

// Carrier visit IDs in OccupancyData are *journey-attached* references; the
// CarrierVisitStore is the source of truth for human-readable names (vessel
// name + voyage, train ID, etc.). Returns undefined when the store hasn't
// hydrated the visit yet, in which case the caller falls back to showing the
// carrier *type* alone.
const resolveCarrierVisitName = (
  items: Array<{ id: number; name: string }>,
  visitId: number | null | undefined,
): string | undefined => {
  if (visitId == null) return undefined
  return items.find(v => v.id === visitId)?.name
}

interface BlockProps {
  occ: OcuppancyWithFilterData
  operatorName: string | undefined
  inboundCarrierName: string | undefined
  outboundCarrierName: string | undefined
  t: (key: string, fallback: string) => string
}

const OccupancyTooltipBlock: FC<BlockProps> = ({
  occ,
  operatorName,
  inboundCarrierName,
  outboundCarrierName,
  t,
}) => {
  const direction = inferDirection(occ.inboundCarrierType, occ.outboundCarrierType)
  const flags = collectCargoFlags(occ)

  // The container number is the primary anchor — fall back to "Container #<id>"
  // when the master data hasn't arrived yet (mirrors the AccumulatorRow
  // pattern elsewhere in this module).
  const primary =
    occ.containerNumber ??
    t('containerWithId', 'Container #{id}').replace('{id}', String(occ.containerId))

  return (
    <Stack gap={0.5}>
      <Typography variant='subtitle2' sx={{ color: 'inherit' }}>
        {primary}
        {occ.isoCode ? (
          <Typography component='span' variant='caption' sx={{ ml: 0.75, opacity: 0.75 }}>
            {occ.isoCode}
          </Typography>
        ) : null}
      </Typography>

      <Stack gap={0.25}>
        <TooltipRow label={t('slotTooltipPOD', 'POD')} value={occ.portOfDischarge ?? undefined} />
        <TooltipRow
          label={t('slotTooltipInbound', 'Inbound')}
          value={formatCarrier(occ.inboundCarrierType, inboundCarrierName)}
        />
        <TooltipRow
          label={t('slotTooltipOutbound', 'Outbound')}
          value={formatCarrier(occ.outboundCarrierType, outboundCarrierName)}
        />
        <TooltipRow label={t('slotTooltipOperator', 'Operator')} value={operatorName} />
        {direction !== undefined && (
          <TooltipRow
            label={t('slotTooltipDirection', 'Direction')}
            value={directionLabel(direction, t)}
          />
        )}
      </Stack>

      {hasAnyCargoFlag(flags) && (
        <Stack direction='row' gap={0.5} flexWrap='wrap' sx={{ mt: 0.25 }}>
          <FlagChips flags={flags} t={t} />
        </Stack>
      )}
    </Stack>
  )
}

interface TooltipRowProps {
  label: string
  value: string | undefined
}

const TooltipRow: FC<TooltipRowProps> = ({ label, value }) => {
  // Skip rows with no data rather than showing "POD: —". Empty rows in a
  // hover tooltip add noise without adding information; the planner already
  // knows a field is missing if we don't print it.
  if (!value) return null
  return (
    <Stack direction='row' gap={1} alignItems='baseline'>
      <Typography
        variant='caption'
        sx={{ minWidth: 64, opacity: 0.75, color: 'inherit', flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography
        variant='body2'
        sx={{ color: 'inherit', wordBreak: 'break-word', flex: 1 }}
      >
        {value}
      </Typography>
    </Stack>
  )
}

const formatCarrier = (
  carrierType: CarrierType | null | undefined,
  carrierName: string | undefined,
): string | undefined => {
  if (!carrierType && !carrierName) return undefined
  if (carrierType && carrierName) return `${carrierType} · ${carrierName}`
  return carrierName ?? carrierType ?? undefined
}

const directionLabel = (
  direction: ContainerDirection,
  t: (key: string, fallback: string) => string,
): string => {
  // Map form (rather than switch) keeps TS exhaustiveness obvious to readers
  // and to the linter, with no implicit fallthrough.
  const labels: Record<ContainerDirection, string> = {
    import: t('slotTooltipDirectionImport', 'Import'),
    export: t('slotTooltipDirectionExport', 'Export'),
    tranship: t('slotTooltipDirectionTranship', 'Transshipment'),
  }
  return labels[direction]
}

interface FlagChipsProps {
  flags: ContainerCargoFlags
  t: (key: string, fallback: string) => string
}

const FlagChips: FC<FlagChipsProps> = ({ flags, t }) => {
  const chips: ReactNode[] = []
  if (flags.reefer) {
    chips.push(<TooltipChip key='reefer' label={t('slotTooltipFlagReefer', 'Reefer')} />)
  }
  if (flags.dangerous) {
    chips.push(<TooltipChip key='dg' label={t('slotTooltipFlagDangerous', 'DG')} />)
  }
  if (flags.outOfGauge) {
    chips.push(<TooltipChip key='oog' label={t('slotTooltipFlagOOG', 'OOG')} />)
  }
  if (flags.empty) {
    chips.push(<TooltipChip key='empty' label={t('slotTooltipFlagEmpty', 'Empty')} />)
  }
  return <>{chips}</>
}

const TooltipChip: FC<{ label: string }> = ({ label }) => (
  <Chip
    label={label}
    size='small'
    sx={{
      // Tooltip body is dark — give chips a translucent light background so
      // they read as foreground without competing with the slot's palette
      // colour bleed.
      height: 18,
      fontSize: 11,
      bgcolor: 'rgba(255,255,255,0.15)',
      color: 'inherit',
      '& .MuiChip-label': { px: 0.75 },
    }}
  />
)
